import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { IPrintTemplatePosition, PrintTemplateType } from './print-template.model';

/**
 * The layout a new template starts from, fetched rather than held here.
 *
 * Those coordinates belong to the renderer — keeping a second copy in the panel meant the two drifted
 * the moment either side was touched. One array per product type: the windows a template is created
 * with, which land in `positions["3x2"].horizontal` while every other combination is stored as null.
 */
@Injectable({ providedIn: 'root' })
export class PrintTemplateDefaults {

    /** Keyed by url, because the CMS and the admin panel ask different endpoints for the same thing. */
    private cache: { [url: string]: Promise<{ [type: string]: IPrintTemplatePosition[] }> } = {};

    constructor(private httpClient: HttpClient) {
    }

    /** Admin panel: not campaign scoped, the layouts are a property of the product. */
    adminUrl(): string {
        return '/admin/print-template/default-positions';
    }

    /** CMS: the same body, through a route the campaign session can reach. */
    cmsUrl(campaignId: number | string): string {
        return '/cms/campaign/crud/' + campaignId + '/print-template/custom/default-positions';
    }

    /**
     * Fetched once per url and kept — it changes when a die-cut changes, which is not something that
     * happens while a panel is open.
     */
    load(url: string): Promise<{ [type: string]: IPrintTemplatePosition[] }> {
        if (!this.cache[ url ]) {
            this.cache[ url ] = this.httpClient.get<any>(url).toPromise();
        }

        return this.cache[ url ];
    }

    /**
     * The starting windows for one product. Rejects rather than inventing any: a template laid out
     * from numbers the renderer does not know about is worse than one that cannot be created.
     */
    async forType(url: string, type: PrintTemplateType): Promise<IPrintTemplatePosition[]> {
        const defaults = await this.load(url);
        const positions = defaults ? defaults[ type ] : null;

        if (!positions || !positions.length) {
            throw { error: { message: 'No starting layout for ' + type + ' came back from the API' } };
        }

        return positions.map((position: IPrintTemplatePosition) => ({ ...position }));
    }
}
